// @ts-nocheck
/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars */
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Receipt, 
  Download, 
  CheckCircle, 
  AlertCircle, 
  Clock,
  ChevronRight,
  ChevronLeft,
  Loader2,
  FileText,
  Filter
} from "lucide-react";

// FIREBASE & CONTEXT
import { collection, query, onSnapshot } from "firebase/firestore";
import { db } from "../../config/firebase"; 
import { useAuth } from "../../contexts/AuthContext"; 

const FaturasPage = () => {
  const { companyInfo } = useAuth();
  const brandColor = "#00a99d";

  // 🟢 Tipagem inserida para zerar problemas do TypeScript
  const [invoices, setInvoices] = useState(/** @type {any[]} */ ([]));
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("TODOS");

  // 🟢 States de Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // ============================================================================
  // BUSCA DE RECIBOS (BOLETOS DE REPASSE B2B GERADOS PELO ASAAS)
  // ============================================================================
  useEffect(() => {
    if (!companyInfo?.id) return;

    // Busca os recibos dentro do documento da Imobiliária logada
    const faturasRef = collection(db, "companies", companyInfo.id, "faturas");
    const qInvoices = query(faturasRef);

    const unsubInvoices = onSnapshot(qInvoices, (snapshot) => {
      const fetchedInvoices = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Ordenação local por data de vencimento (mais recentes primeiro)
      fetchedInvoices.sort((a, b) => {
        const dateA = a.dueDate ? new Date(a.dueDate).getTime() : 0;
        const dateB = b.dueDate ? new Date(b.dueDate).getTime() : 0;
        return dateB - dateA;
      });

      setInvoices(fetchedInvoices);
      setLoading(false);
    }, (error) => {
      console.error("Erro ao buscar recibos:", error);
      setLoading(false);
    });

    return () => unsubInvoices();
  }, [companyInfo?.id]);

  // 🟢 Reseta a página sempre que o filtro de abas ou quantidade de itens mudar
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, itemsPerPage]);

  // ============================================================================
  // LÓGICA DE FILTROS E KPIS
  // ============================================================================
  const filteredInvoices = invoices.filter(invoice => {
    if (statusFilter === "TODOS") return true;
    return invoice.status === statusFilter;
  });

  // KPIs sempre olham para a base total (invoices) para não corromper a matemática ao paginar
  const totalPendente = invoices
    .filter(inv => inv.status === "PENDING" || inv.status === "OVERDUE")
    .reduce((acc, curr) => acc + (curr.value || 0), 0);

  const faturasAbertasCount = invoices.filter(inv => inv.status === "PENDING" || inv.status === "OVERDUE").length;

  // 🟢 Lógica de Fatiamento (Paginação Client-Side)
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredInvoices.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);

  const nextPage = () => { if (currentPage < totalPages) setCurrentPage(prev => prev + 1); };
  const prevPage = () => { if (currentPage > 1) setCurrentPage(prev => prev - 1); };

  // ============================================================================
  // RENDERIZADOR DE STATUS VISUAL
  // ============================================================================
  const renderBadgeStatus = (status) => {
    switch (status) {
      case 'RECEIVED':
      case 'CONFIRMED':
      case 'RECEIVED_IN_CASH':
        return (
          <div className="flex items-center gap-1.5 text-green-700 bg-green-50 px-2.5 py-1 rounded-lg border border-green-200 font-semibold text-xs w-max">
            <CheckCircle className="w-3.5 h-3.5" /> Pago
          </div>
        );
      case 'PENDING':
        return (
          <div className="flex items-center gap-1.5 text-amber-700 bg-amber-50 px-2.5 py-1 rounded-lg border border-amber-200 font-semibold text-xs w-max">
            <Clock className="w-3.5 h-3.5" /> Aguardando Pagamento
          </div>
        );
      case 'OVERDUE':
        return (
          <div className="flex items-center gap-1.5 text-red-700 bg-red-50 px-2.5 py-1 rounded-lg border border-red-200 font-semibold text-xs w-max">
            <AlertCircle className="w-3.5 h-3.5" /> Vencido
          </div>
        );
      case 'CANCELED':
        return (
          <div className="flex items-center gap-1.5 text-slate-700 bg-slate-100 px-2.5 py-1 rounded-lg border border-slate-300 font-semibold text-xs w-max">
            <AlertCircle className="w-3.5 h-3.5" /> Cancelada
          </div>
        );
      default:
        return (
          <div className="flex items-center gap-1.5 text-slate-700 bg-slate-100 px-2.5 py-1 rounded-lg border border-slate-200 font-semibold text-xs w-max">
            <Clock className="w-3.5 h-3.5" /> Em Processamento
          </div>
        );
    }
  };

  // ============================================================================
  // SKELETON LOADER
  // ============================================================================
  if (loading) {
    return (
      <div className="p-4 sm:p-5 xl:p-8 w-full animate-pulse bg-[#f8fafc] min-h-screen space-y-6">
        <div className="flex items-center gap-4 mb-2">
           <div className="w-14 h-14 bg-slate-200 rounded-2xl shrink-0"></div>
           <div className="space-y-2"><div className="h-6 bg-slate-200 rounded w-48"></div><div className="h-4 bg-slate-200 rounded w-96"></div></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
           {Array(3).fill(0).map((_, i) => (
             <div key={i} className="bg-white p-5 rounded-2xl border border-slate-200 h-24 flex items-center justify-between">
                <div className="space-y-2"><div className="h-4 bg-slate-100 rounded w-32"></div><div className="h-6 bg-slate-200 rounded w-24"></div></div>
                <div className="w-12 h-12 bg-slate-100 rounded-full"></div>
             </div>
           ))}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mt-6">
          <div className="bg-slate-50 border-b border-slate-200 h-16"></div>
          {Array(5).fill(0).map((_, i) => (
            <div key={i} className="flex justify-between items-center p-6 border-b border-slate-50">
              <div className="space-y-2"><div className="h-4 bg-slate-200 rounded w-32"></div><div className="h-3 bg-slate-100 rounded w-24"></div></div>
              <div className="space-y-2"><div className="h-4 bg-slate-200 rounded w-24"></div><div className="h-3 bg-slate-100 rounded w-16"></div></div>
              <div className="h-6 bg-slate-200 rounded-full w-28"></div>
              <div className="flex gap-2">
                <div className="h-8 bg-slate-200 rounded w-10"></div>
                <div className="h-8 bg-slate-200 rounded w-28"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-5 xl:p-8 w-full font-sans animate-fade-in bg-[#f8fafc] min-h-full space-y-6 relative">
      
      {/* BREADCRUMB E HEADER */}
      <div className="mb-2">
        <div className="flex items-center text-sm text-slate-500 font-medium mb-2">
          <Link to="/dashboard" className="hover:text-teal-600 transition-colors">Dashboard</Link>
          <ChevronRight className="w-4 h-4 mx-1" />
          <span className="text-slate-800 font-semibold">Recibos e Repasses</span>
        </div>
        
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-teal-50 flex items-center justify-center shrink-0 border border-teal-100/60 shadow-sm">
              <Receipt className="w-7 h-7" style={{ color: brandColor }} />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">
                Central de Recibos
              </h1>
              <p className="text-sm font-medium text-slate-500 mt-1">
                Acesse seus boletos de repasse e relatórios consolidados de fechamento.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CARDS DE KPI */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between hover:border-red-200 transition-colors">
          <div>
            <p className="text-sm font-medium text-slate-500">Total Pendente</p>
            <h3 className="text-2xl font-bold text-slate-800 mt-1">
              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalPendente)}
            </h3>
          </div>
          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
        </div>
        
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between hover:border-amber-200 transition-colors">
          <div>
            <p className="text-sm font-medium text-slate-500">Recibos em Aberto</p>
            <h3 className="text-2xl font-bold text-slate-800 mt-1">
              {faturasAbertasCount}
            </h3>
          </div>
          <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center">
            <Clock className="w-6 h-6 text-amber-600" />
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border-slate-200 shadow-sm flex items-center justify-between border-l-4" style={{ borderLeftColor: faturasAbertasCount === 0 ? brandColor : '#ef4444' }}>
          <div>
            <p className="text-sm font-medium text-slate-500">Status Geral</p>
            <h3 className="text-lg font-bold text-slate-800 mt-1">
              {faturasAbertasCount === 0 ? "Tudo em dia!" : "Atenção necessária"}
            </h3>
          </div>
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${faturasAbertasCount === 0 ? 'bg-teal-50' : 'bg-red-50'}`}>
            {faturasAbertasCount === 0 ? <CheckCircle className="w-6 h-6 text-teal-600" /> : <AlertCircle className="w-6 h-6 text-red-600" />}
          </div>
        </div>
      </div>

      {/* PAINEL DE RECIBOS */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
        
        {/* BARRA DE FERRAMENTAS / FILTROS */}
        <div className="p-4 xl:p-5 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-center gap-4 bg-slate-50/50 shrink-0">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <Filter className="w-4 h-4 text-slate-400" />
            Filtrar por Status:
          </div>
          <div className="flex gap-2 w-full sm:w-auto overflow-x-auto pb-2 sm:pb-0 custom-scrollbar">
            {['TODOS', 'PENDING', 'RECEIVED', 'OVERDUE'].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-2 rounded-xl text-sm font-bold transition-all whitespace-nowrap border ${
                  statusFilter === status 
                    ? 'bg-teal-600 text-white border-teal-600 shadow-sm' 
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
              >
                {status === 'TODOS' ? 'Todos os Recibos' : 
                 status === 'PENDING' ? 'Pendentes' : 
                 status === 'RECEIVED' ? 'Pagos' : 'Vencidos'}
              </button>
            ))}
          </div>
        </div>

        {/* TABELA DE RECIBOS */}
        <div className="overflow-x-auto min-h-[300px] flex-1">
          <table className="w-full text-left border-collapse whitespace-nowrap">
            <thead>
              <tr className="text-xs font-semibold text-slate-500 border-b border-slate-100 bg-white uppercase tracking-wider">
                <th className="px-6 py-4">Referência / Vencimento</th>
                <th className="px-6 py-4">Valor Total</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Ações e Documentos</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {currentItems.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-20 text-center">
                    <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Receipt className="w-8 h-8 text-slate-300" />
                    </div>
                    <p className="text-slate-800 font-bold text-base">Nenhum recibo encontrado nesta visão</p>
                    <p className="text-slate-500 text-sm mt-1">Altere o filtro de abas ou realize novos repasses.</p>
                  </td>
                </tr>
              ) : (
                currentItems.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <p className="font-bold text-slate-800 text-sm uppercase">
                        {invoice.description || "Recibo de Serviços"}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                        Vence em: {invoice.dueDate ? new Date(invoice.dueDate + 'T12:00:00Z').toLocaleDateString('pt-BR') : "N/A"}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-bold text-slate-800 text-sm">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(invoice.value || 0)}
                      </p>
                      <p className="text-[10px] text-slate-500 font-medium uppercase mt-0.5">
                        {invoice.billingType === 'UNDEFINED' ? 'BOLETO / PIX' : (invoice.billingType || 'BOLETO')}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      {renderBadgeStatus(invoice.status)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {/* BOTÃO DE RELATÓRIO PDF */}
                        <button 
                          title="Fatura PDF / Detalhamento"
                          className="inline-flex items-center justify-center p-2 text-slate-500 hover:text-blue-600 bg-white hover:bg-blue-50 border border-slate-200 hover:border-blue-200 rounded-lg transition-colors shadow-sm disabled:opacity-50"
                          onClick={() => window.open(invoice.invoiceUrl || '#', '_blank')}
                          disabled={!invoice.invoiceUrl}
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                        
                        {/* BOTÃO DE BOLETO DIRETO DO ASAAS */}
                        <button 
                          className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold rounded-lg transition-colors shadow-sm disabled:opacity-50 ${
                            invoice.status === 'RECEIVED' || invoice.status === 'CONFIRMED' 
                              ? 'bg-slate-100 text-slate-700 hover:bg-slate-200 border-slate-200' 
                              : 'bg-teal-50 text-teal-700 hover:bg-teal-100 border-teal-200'
                          }`}
                          onClick={() => window.open(invoice.bankSlipUrl || invoice.invoiceUrl || '#', '_blank')}
                          disabled={!invoice.bankSlipUrl && !invoice.invoiceUrl}
                        >
                          {invoice.status === 'RECEIVED' || invoice.status === 'CONFIRMED' ? <CheckCircle className="w-4 h-4" /> : <Download className="w-4 h-4" />}
                          {invoice.status === 'RECEIVED' || invoice.status === 'CONFIRMED' ? 'Ver Fatura' : 'Acessar Boleto'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* 🟢 CONTROLES DE PAGINAÇÃO VIVOS (FOOTER) */}
        {filteredInvoices.length > 0 && (
          <div className="p-4 sm:p-5 border-t border-slate-100 bg-slate-50/50 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-500 shrink-0">
            <span className="font-medium text-center md:text-left">
              Mostrando <strong className="text-slate-800">{indexOfFirstItem + 1}</strong> a <strong className="text-slate-800">{Math.min(indexOfLastItem, filteredInvoices.length)}</strong> de <strong className="text-slate-800">{filteredInvoices.length}</strong> recibos
            </span>
            
            <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
              <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-slate-200 shadow-sm w-full sm:w-auto justify-center">
                <span className="font-medium">Itens:</span>
                <select 
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className="bg-transparent border-none font-bold text-teal-600 focus:ring-0 cursor-pointer text-sm outline-none"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center gap-2 justify-center w-full sm:w-auto">
                  <button 
                    onClick={prevPage}
                    disabled={currentPage === 1}
                    className="p-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-50 disabled:hover:bg-white transition-colors shadow-sm"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="text-sm font-bold text-slate-700 px-3 py-1 bg-white border border-slate-200 rounded-lg shadow-sm">
                    {currentPage} / {totalPages}
                  </span>
                  <button 
                    onClick={nextPage}
                    disabled={currentPage >= totalPages}
                    className="p-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-50 disabled:hover:bg-white transition-colors shadow-sm"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default FaturasPage;